import { noop } from 'lodash';
import { createContext, type ReactNode, useCallback, useContext, useMemo, useRef, useState } from 'react';

import { reportErrorToBugsnag } from '@air/utils-error';

import { type PercentageRect } from '~/components/EditMode/components/BoundingBoxLayerKonva/types';
import { EditModeTaskEventListener } from '~/components/EditMode/components/EditModeTaskEventListener';
import {
  type AssetTransformStatus,
  EDIT_MODE_OPTION_CUSTOM_PROMPT,
  type EditModeOption,
} from '~/components/EditMode/constants';
import { isDevOrTestStage } from '~/swr-hooks/utils';

export {
  MoveMode,
  RefocusMode,
  SelectionMode,
} from '~/components/EditMode/components/EditModeBoundingBox/EditModeBoundingBox';

import {
  MoveMode,
  RefocusMode,
  SelectionMode,
} from '~/components/EditMode/components/EditModeBoundingBox/EditModeBoundingBox';

export type SetAspectRatioOptions = {
  preserveBox?: boolean;
};

type EditModeContextType = {
  isEditModeEnabled: boolean;
  setIsEditModeEnabled: (isEditModeEnabled: boolean) => void;
  currentEditModeOption: EditModeOption | null;
  setCurrentEditModeOption: (currentEditModeOption: EditModeOption | null) => void;
  assetTransformStatus: AssetTransformStatus;
  setAssetTransformStatus: (assetTransformStatus: AssetTransformStatus) => void;
  boundingBox: {
    box: PercentageRect | null;
    setBox: (box: PercentageRect | null) => void;
    aspectRatio: number | undefined;
    setAspectRatio: (aspectRatio: number | undefined, options?: SetAspectRatioOptions) => void;
    preserveAspectRatioBox: boolean;
    showGuideGrid: boolean;
    setShowGuideGrid: (showGuideGrid: boolean) => void;
    showDimensions: boolean;
    setShowDimensions: (showDimensions: boolean) => void;
    selectionMode: SelectionMode;
    setSelectionMode: (selectionMode: SelectionMode) => void;
    moveMode: MoveMode;
    setMoveMode: (moveMode: MoveMode) => void;
    refocusMode: RefocusMode;
    setRefocusMode: (refocusMode: RefocusMode) => void;
    scale: number;
    setScale: (scale: number) => void;
    containerRef: React.RefObject<HTMLDivElement>;
  };
  imageOffset: { x: number; y: number };
  setImageOffset: (offset: { x: number; y: number }) => void;
  visualizerRef: React.RefObject<HTMLDivElement>;
  imageRef: React.RefObject<HTMLImageElement>;
};

const initialState: EditModeContextType = {
  isEditModeEnabled: false,
  setIsEditModeEnabled: () => noop,
  currentEditModeOption: EDIT_MODE_OPTION_CUSTOM_PROMPT,
  setCurrentEditModeOption: () => noop,
  assetTransformStatus: 'idle',
  setAssetTransformStatus: () => noop,
  boundingBox: {
    box: null,
    setBox: () => noop,
    aspectRatio: undefined,
    setAspectRatio: () => noop,
    preserveAspectRatioBox: false,
    showGuideGrid: false,
    setShowGuideGrid: () => noop,
    showDimensions: false,
    setShowDimensions: () => noop,
    selectionMode: SelectionMode.FREE,
    setSelectionMode: () => noop,
    moveMode: MoveMode.BOX,
    setMoveMode: () => noop,
    refocusMode: RefocusMode.BOX,
    setRefocusMode: () => noop,
    scale: 1,
    setScale: () => noop,
    containerRef: { current: null },
  },
  imageOffset: { x: 0, y: 0 },
  setImageOffset: () => noop,
  visualizerRef: { current: null },
  imageRef: { current: null },
};

export const EditModeProvider = ({ children }: { children: ReactNode }) => {
  const [isEditModeEnabled, setIsEditModeEnabled] = useState(false);
  const [currentEditModeOption, setCurrentEditModeOption] = useState<EditModeOption | null>(
    EDIT_MODE_OPTION_CUSTOM_PROMPT,
  );
  const [assetTransformStatus, setAssetTransformStatus] = useState<AssetTransformStatus>('idle');
  const [boundingBox, setBoundingBox] = useState<PercentageRect | null>(null);
  const [boundingBoxAspectRatio, setBoundingBoxAspectRatio] = useState<number | undefined>(undefined);
  const [preserveAspectRatioBox, setPreserveAspectRatioBox] = useState(false);
  const [boundingBoxShowGuideGrid, setBoundingBoxShowGuideGrid] = useState(false);
  const [boundingBoxShowDimensions, setBoundingBoxShowDimensions] = useState(false);
  const [boundingBoxSelectionMode, setBoundingBoxSelectionMode] = useState<SelectionMode>(SelectionMode.FREE);
  const [boundingBoxMoveMode, setBoundingBoxMoveMode] = useState<MoveMode>(MoveMode.BOX);
  const [boundingBoxRefocusMode, setBoundingBoxRefocusMode] = useState<RefocusMode>(RefocusMode.BOX);
  const [boundingBoxScale, setBoundingBoxScale] = useState<number>(1);
  const [imageOffset, setImageOffset] = useState<{ x: number; y: number }>({ x: 0, y: 0 });

  const visualizerRef = useRef(null);
  const imageRef = useRef(null);
  const boundingBoxContainerRef = useRef(null);

  const applyBoundingBoxAspectRatio = useCallback(
    (aspectRatio: number | undefined, options?: SetAspectRatioOptions): void => {
      setPreserveAspectRatioBox(options?.preserveBox ?? false);
      setBoundingBoxAspectRatio(aspectRatio);
    },
    [],
  );

  const value = useMemo<EditModeContextType>(
    () => ({
      isEditModeEnabled,
      setIsEditModeEnabled,
      currentEditModeOption,
      setCurrentEditModeOption,
      assetTransformStatus,
      setAssetTransformStatus,
      boundingBox: {
        box: boundingBox,
        setBox: setBoundingBox,
        aspectRatio: boundingBoxAspectRatio,
        setAspectRatio: applyBoundingBoxAspectRatio,
        preserveAspectRatioBox,
        showGuideGrid: boundingBoxShowGuideGrid,
        setShowGuideGrid: setBoundingBoxShowGuideGrid,
        showDimensions: boundingBoxShowDimensions,
        setShowDimensions: setBoundingBoxShowDimensions,
        selectionMode: boundingBoxSelectionMode,
        setSelectionMode: setBoundingBoxSelectionMode,
        moveMode: boundingBoxMoveMode,
        setMoveMode: setBoundingBoxMoveMode,
        refocusMode: boundingBoxRefocusMode,
        setRefocusMode: setBoundingBoxRefocusMode,
        scale: boundingBoxScale,
        setScale: setBoundingBoxScale,
        containerRef: boundingBoxContainerRef,
      },
      imageOffset,
      setImageOffset,
      visualizerRef,
      imageRef,
    }),
    [
      isEditModeEnabled,
      currentEditModeOption,
      assetTransformStatus,
      boundingBox,
      boundingBoxAspectRatio,
      preserveAspectRatioBox,
      boundingBoxShowGuideGrid,
      boundingBoxShowDimensions,
      boundingBoxSelectionMode,
      boundingBoxMoveMode,
      boundingBoxRefocusMode,
      boundingBoxScale,
      imageOffset,
      applyBoundingBoxAspectRatio,
    ],
  );

  return (
    <EditModeContext.Provider value={value}>
      <EditModeTaskEventListener />
      {children}
    </EditModeContext.Provider>
  );
};

export const useEditMode = () => {
  const context = useContext(EditModeContext);

  if (!context) {
    if (isDevOrTestStage()) {
      throw new Error('useEditMode must be used within an EditModeProvider');
    } else {
      reportErrorToBugsnag({
        error: new Error('useEditMode must be used within an EditModeProvider'),
        context: 'useEditMode must be used within an EditModeProvider',
      });
    }
  }

  return context;
};

export const EditModeContext = createContext<EditModeContextType>(initialState);
